import { useCallback, useEffect, useReducer } from 'react';

import nanoid from 'nanoid';

import {
  FileHandlerConfig,
  ProcessResult,
} from '../FileHandlers/FileHandlerConfig';
import { createFileValidator } from '../FileValidator/createFileValidator';
import type { FileError } from '../FileValidator/types';

type FileUploadTaskState = Array<FileTask>;

const initialState: FileUploadTaskState = [];

type FileUploadTaskAction =
  | {
      type: 'add';
      payload: { tasks: Array<File> };
    }
  | {
      type: 'remove';
      payload: { ids: Array<string> };
    }
  | {
      type: 'retry';
      payload: { ids: Array<string> };
    }
  | {
      type: 'progress';
      payload: {
        id: string;
        loaded: number;
        total: number;
        lengthComputable: boolean;
      };
    }
  | {
      type: 'success';
      payload: { id: string; result: ProcessResult };
    }
  | {
      type: 'error';
      payload: {
        id: string;
        error: string | FileError;
      };
    };

export function useFileReducer(maxFiles: number) {
  return useReducer(
    (
      state: FileUploadTaskState,
      action: FileUploadTaskAction
    ): FileUploadTaskState => {
      switch (action.type) {
        case 'add': {
          const { tasks } = action.payload;

          const fileTasks = tasks.map((file) => ({
            id: nanoid(),
            file,
            status: 'pending' as const,
          })) as Array<FileUploadTaskPending>;

          // single task - always take only the first file and replace existing
          if (maxFiles === 1) {
            // If multiple files were dropped, only take the first one
            const singleFileTask = fileTasks[0];
            if (!singleFileTask) {
              return state;
            }

            return [singleFileTask];
          } else {
            if (fileTasks.length + state.length > maxFiles) {
              throw new Error('File tasks must be greater than ' + maxFiles);
            }
            return [...state, ...fileTasks];
          }
        }
        case 'remove': {
          const { ids } = action.payload;
          return state.filter((task) => !ids.includes(task.id));
        }
        case 'retry': {
          const { ids } = action.payload;
          const idSet = new Set(ids);
          return state.map((task) =>
            idSet.has(task.id)
              ? ({
                  id: task.id,
                  file: task.file,
                  status: 'pending' as const,
                } as FileUploadTaskPending)
              : task
          );
        }
        case 'progress': {
          const { id, loaded, total, lengthComputable } = action.payload;
          return state.map((task) =>
            task.id === id
              ? ({
                  id: task.id,
                  file: task.file,
                  status: 'progress' as const,
                  loaded,
                  total,
                  lengthComputable,
                } as FileUploadTaskProgress)
              : task
          );
        }
        case 'success': {
          const { id, result } = action.payload;
          return state.map((task) =>
            task.id === id
              ? ({
                  id: task.id,
                  file: task.file,
                  status: 'success' as const,
                  result,
                } as FileUploadTaskSuccess)
              : task
          );
        }
        case 'error': {
          const { id, error } = action.payload;
          return state.map((task) =>
            task.id === id
              ? ({
                  id: task.id,
                  file: task.file,
                  status: 'error' as const,
                  error,
                } as FileUploadTaskError)
              : task
          );
        }
        default:
          return state;
      }
    },
    initialState
  );
}

export interface FileDropCallbacks {
  addFileTasks: (files: Array<File>) => void;
  removeFileTasks: (ids: Array<string>) => void;
  retryFileTasks: (ids: Array<string>) => void;
}

interface UseFileDropConfigParams extends FileHandlerConfig {
  maxFileCount: number;
  maxFileSize: number;
}

export function useFileDropConfig({
  maxFileCount = 1,
  maxFileSize,
  mimeTypeMap,
  allMimeTypes,
  extensionMap,
  commonExtensions,
  allExtensions,
}: UseFileDropConfigParams) {
  const [tasks, dispatch] = useFileReducer(maxFileCount);

  const addFileTasks = useCallback(
    (files: Array<File>) => {
      dispatch({ type: 'add', payload: { tasks: files } });
    },
    [dispatch]
  );

  const removeFileTasks = useCallback(
    (ids: Array<string>) => {
      dispatch({ type: 'remove', payload: { ids } });
    },
    [dispatch]
  );

  const retryFileTasks = useCallback(
    (ids: Array<string>) => {
      dispatch({ type: 'retry', payload: { ids } });
    },
    [dispatch]
  );

  const fileValidator = createFileValidator({
    allowedMimeTypes: allMimeTypes,
    maxFileSize,
  });

  useEffect(() => {
    // Process all pending files
    const pendingTasks = tasks.filter(
      (pf): pf is FileUploadTaskPending => pf.status === 'pending'
    );

    if (pendingTasks.length === 0) {
      return;
    }

    pendingTasks.forEach((pendingTask) => {
      const file = pendingTask.file;

      // Validate the file
      const validation = fileValidator(file);
      const validationError = validation.find((v) => v.valid === false);

      if (validationError) {
        if (__DEV__) {
          console.debug('File validation failed:', file.name, validationError);
        }
        dispatch({
          type: 'error',
          payload: {
            id: pendingTask.id,
            error: validationError,
          },
        });
        return;
      }

      const handler = mimeTypeMap.get(file.type);

      if (!handler) {
        if (__DEV__) {
          console.debug('No handler available for file type:', file.type);
        }
        dispatch({
          type: 'error',
          payload: {
            id: pendingTask.id,
            error: 'No handler available for this file type',
          },
        });
        return;
      }

      // Update status to progress
      dispatch({
        type: 'progress',
        payload: {
          id: pendingTask.id,
          loaded: 0,
          total: 0,
          lengthComputable: false,
        },
      });

      handler({
        file,
        onUploadProgress: ({ loaded, total, lengthComputable }) => {
          dispatch({
            type: 'progress',
            payload: {
              id: pendingTask.id,
              loaded,
              total,
              lengthComputable,
            },
          });
        },
      })
        .then((data) => {
          if (__DEV__) {
            console.debug('File upload successful:', file.name);
          }
          dispatch({
            type: 'success',
            payload: {
              id: pendingTask.id,
              result: data,
            },
          });
        })
        .catch((error) => {
          if (__DEV__) {
            console.debug('File upload failed:', file.name, error);
          }
          dispatch({
            type: 'error',
            payload: {
              id: pendingTask.id,
              error: error instanceof Error ? error.message : 'Upload failed',
            },
          });
        });
    });
  }, [fileValidator, tasks, dispatch, mimeTypeMap]);

  return {
    tasks,
    addFileTasks,
    removeFileTasks,
    retryFileTasks,
    maxFileCount,
    maxFileSize,
    mimeTypeMap,
    allMimeTypes,
    extensionMap,
    commonExtensions,
    allExtensions,
  };
}

export type FileDropConfig = ReturnType<typeof useFileDropConfig>;

export type FileTask<T extends ProcessResult = ProcessResult> =
  | FileUploadTaskSuccess<T>
  | FileUploadTaskError
  | FileUploadTaskProgress
  | FileUploadTaskPending;

type GenericFileTask = {
  id: string;
  file: File;
};

export type FileUploadTaskSuccess<T extends ProcessResult = ProcessResult> =
  GenericFileTask & {
    id: string;
    file: File;
    status: 'success';
    progress: never;
    error: never;
    result: T;
  };

export type FileUploadTaskError = GenericFileTask & {
  status: 'error';
  error: string | FileError;
  progress: never;
  result: never;
};

export type FileUploadTaskProgress = GenericFileTask & {
  status: 'progress';
  loaded: number;
  total: number;
  lengthComputable: boolean;
  error: never;
  result: never;
};

export type FileUploadTaskPending = GenericFileTask & {
  status: 'pending';
  progress: never;
  error: never;
  result: never;
};

// Re-export types for convenience
export type {
  FileHandlerConfig,
  FileHandlerGroup,
  MimeTypeHandlerMap,
} from '../FileHandlers/FileHandlerConfig';
